import {ChangeDetectionStrategy, Component, computed, inject, Input} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  OnboardingProgressStatus,
  OnboardingStepProgress,
} from '@app/areas/account/onboarding/services/user-onboarding-api.service';
import {LocalizeTextPipe} from "@shared/pipes/localize-text/localize-text.pipe";
import {AppAuthService} from "@services/core/auth/app-auth.service";
import {hasValue} from "@shared/utils/helpers";

interface StepVm {
  id: string;
  title: string;
  description: string;
  status: OnboardingProgressStatus;
  isCurrent: boolean;
  requiredFields: string[];
}

interface PhaseVm {
  key: string;
  label: string;
  completedCount: number;
  totalCount: number;
  steps: StepVm[];
}

interface OnboardingProgressVm {
  overallPercent: number;
  phases: PhaseVm[];
}

@Component({
  selector: 'app-my-onboarding-progress',
  standalone: true,
  imports: [CommonModule, LocalizeTextPipe],
  templateUrl: './my-onboarding-progress.component.html',
  styleUrls: ['./my-onboarding-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyOnboardingProgressComponent {
  private _progress: OnboardingStepProgress[] = [];
  vm: OnboardingProgressVm | null = null;

  auth = inject(AppAuthService)

  username$ = computed(() => {
    const u = this.auth.user$()?.identityProfile;
    const appleId = u.appleLoginId;
    const username = u.username;

    if (hasValue(appleId)) {
      return 'Apple Login'
    }

    return username;
  })

  @Input()
  set progress(value: OnboardingStepProgress[] | null | undefined) {
    this._progress = value ?? [];
    this.vm = this.buildVm(this._progress);
  }
  get progress(): OnboardingStepProgress[] {
    return this._progress;
  }

  // Map backend phase keys → nice labels
  private readonly phaseLabels: Record<string, string> = {
    enquiry: 'Enquiry',
    assessment: 'Assessment',
    onboarding: 'Onboarding',
    // add more as needed; fallback uses titleCase
  };

  private buildVm(steps: OnboardingStepProgress[]): OnboardingProgressVm {
    if (!steps.length) {
      return { overallPercent: 0, phases: [] };
    }

    const sorted = [...steps].sort(
      (a, b) => (a.step.sequence ?? 0) - (b.step.sequence ?? 0),
    );

    // current step = first non-completed; if all completed → last step
    let currentIdx = sorted.findIndex(
      (s) => s.progress.status !== 'completed',
    );
    if (currentIdx === -1) currentIdx = sorted.length - 1;

    const phasesMap = new Map<string, PhaseVm>();

    sorted.forEach((s, idx) => {
      const phaseKey = s.step.phaseKey ?? 'default';
      let phase = phasesMap.get(phaseKey);
      if (!phase) {
        phase = {
          key: phaseKey,
          label: this.phaseLabels[phaseKey] ?? this.titleCase(phaseKey),
          completedCount: 0,
          totalCount: 0,
          steps: [],
        };
        phasesMap.set(phaseKey, phase);
      }

      const isCompleted = s.progress.status === 'completed';
      if (isCompleted) phase.completedCount++;
      phase.totalCount++;

      const stepVm: StepVm = {
        id: s.step.id,
        title: s.step.title,
        description: s.step.config?.description ?? '',
        status: s.progress.status,
        isCurrent: idx === currentIdx,
        requiredFields: s.step.config?.requiredFields ?? [],
      };

      phase.steps.push(stepVm);
    });

    const totalSteps = sorted.length;
    const completedSteps = sorted.filter(
      (s) => s.progress.status === 'completed',
    ).length;
    const overallPercent = Math.round((completedSteps / totalSteps) * 100);

    return {
      overallPercent,
      phases: Array.from(phasesMap.values()),
    };
  }

  protected phasePercent(phase: PhaseVm): number {
    if (!phase.totalCount) return 0;
    return Math.round((phase.completedCount / phase.totalCount) * 100);
  }

  private titleCase(key: string): string {
    return key
      .replace(/[-_]/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
  }
}
